import { CellValueType } from '@univerjs/core';
import { univerTheme, EDITABLE_COMPONENTS, LOCKED_COMPONENTS, TEXT_COMPONENTS, DATETIME_SPECIFIC_SENTINEL, NUMBER_SPECIFIC_SENTINEL, buildNumberPattern } from './component-rules';
import type { ComponentType } from './component-rules';

export interface DbSheet {
  name: string;
  rows: Array<{ columns?: Array<any> } | null>;
}

const FIRST_COLUMN_WIDTH = 400;
const DEFAULT_COLUMN_WIDTH = 120;
const MIN_COLUMNS = 12;
const MIN_ROWS = 50;

const THREAD_COMMENT_PLUGIN = 'SHEET_UNIVER_THREAD_COMMENT_PLUGIN';
const COMMENT_COMPONENTS: Partial<Record<ComponentType, string>> = {
  HEADER: 'HEADER',
  HEADERTEXT: 'HEADERTEXT',
};

function columnIndexToLetters(col: number): string {
  let n = col;
  let out = '';
  while (n >= 0) {
    out = String.fromCharCode((n % 26) + 65) + out;
    n = Math.floor(n / 26) - 1;
  }
  return out;
}

function toA1(row: number, col: number): string {
  return `${columnIndexToLetters(col)}${row + 1}`;
}

// Must match @univerjs/thread-comment's getDT(); other formats show raw ISO.
function formatCommentDate(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// PrimeVue CSS-var tokens → hex; mirrors backend sheet_style_manager.py.
const FONT_COLOR_MAP: Record<string, string> = {
  'var(--surface-100)': '#f1f5f9',
  'var(--surface-200)': '#e2e8f0',
  'var(--surface-300)': '#cbd5e1',
  'var(--surface-400)': '#94a3b8',
  'var(--surface-500)': '#64748b',
  'var(--surface-600)': '#475569',
  'var(--surface-700)': '#334155',
  'var(--surface-800)': '#1e293b',
  'var(--red-100)': '#ffd0ce',
  'var(--red-200)': '#ffaca7',
  'var(--red-300)': '#ff8780',
  'var(--red-400)': '#ff6259',
  'var(--red-500)': '#ff3d32',
  'var(--red-600)': '#d9342b',
  'var(--red-700)': '#b32b23',
  'var(--red-800)': '#8c221c',
  'var(--orange-100)': '#feddc7',
  'var(--orange-200)': '#fcc39b',
  'var(--orange-300)': '#fba86f',
  'var(--orange-400)': '#fa8e42',
  'var(--orange-500)': '#f97316',
  'var(--orange-600)': '#d46213',
  'var(--orange-700)': '#ae510f',
  'var(--orange-800)': '#893f0c',
  'var(--yellow-100)': '#faedc4',
  'var(--yellow-200)': '#f6de95',
  'var(--yellow-300)': '#f2d066',
  'var(--yellow-400)': '#eec137',
  'var(--yellow-500)': '#eab308',
  'var(--yellow-600)': '#c79807',
  'var(--yellow-700)': '#a47d06',
  'var(--yellow-800)': '#816204',
  'var(--green-100)': '#caf1d8',
  'var(--green-200)': '#a0e6ba',
  'var(--green-300)': '#76db9b',
  'var(--green-400)': '#4cd07d',
  'var(--green-500)': '#22c55e',
  'var(--green-600)': '#1da750',
  'var(--green-700)': '#188a42',
  'var(--green-800)': '#136c34',
  'var(--cyan-100)': '#c3edf5',
  'var(--cyan-200)': '#94e0ed',
  'var(--cyan-300)': '#65d2e4',
  'var(--cyan-400)': '#35c4dc',
  'var(--cyan-500)': '#06b6d4',
  'var(--cyan-600)': '#059bb4',
  'var(--cyan-700)': '#047f94',
  'var(--cyan-800)': '#036475',
  'var(--blue-100)': '#d0e1fd',
  'var(--blue-200)': '#abc9fb',
  'var(--blue-300)': '#85b2f9',
  'var(--blue-400)': '#609af8',
  'var(--blue-500)': '#3b82f6',
  'var(--blue-600)': '#326fd1',
  'var(--blue-700)': '#295bac',
  'var(--blue-800)': '#204887',
  'var(--indigo-100)': '#dadafc',
  'var(--indigo-200)': '#bcbdf9',
  'var(--indigo-300)': '#9ea0f6',
  'var(--indigo-400)': '#8183f4',
  'var(--indigo-500)': '#6366f1',
  'var(--indigo-600)': '#5457cd',
  'var(--indigo-700)': '#4547a9',
  'var(--indigo-800)': '#363885',
  'var(--purple-100)': '#ead6fd',
  'var(--purple-200)': '#dab6fc',
  'var(--purple-300)': '#c996fa',
  'var(--purple-400)': '#b975f9',
  'var(--purple-500)': '#a855f7',
  'var(--purple-600)': '#8f48d2',
  'var(--purple-700)': '#763cad',
  'var(--purple-800)': '#5c2f88',
};

const HEX_TO_VAR_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(FONT_COLOR_MAP).map(([k, v]) => [v.toLowerCase(), k]),
);

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Closest PrimeVue CSS variable by Euclidean RGB distance; mirrors backend
 *  sheet_style_manager.py. */
export function mapHexToPrimeVueColor(hex: string): string {
  if (hex.startsWith('var(')) return hex;
  const lower = hex.toLowerCase();
  if (HEX_TO_VAR_MAP[lower]) return HEX_TO_VAR_MAP[lower];

  const [r1, g1, b1] = hexToRgb(lower);
  let closest = hex;
  let minDist = Infinity;
  for (const [pvHex, pvVar] of Object.entries(HEX_TO_VAR_MAP)) {
    const [r2, g2, b2] = hexToRgb(pvHex);
    const dist = Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
    if (dist < minDist) {
      minDist = dist;
      closest = pvVar;
    }
  }
  return closest;
}

const HORIZONTAL_ALIGN_MAP: Record<string, number> = { left: 1, center: 2, right: 3 };

const BORDER_SIDE_KEY: Record<string, 't' | 'b' | 'l' | 'r'> = { top: 't', bottom: 'b', left: 'l', right: 'r' };
const BORDER_STYLE_MAP: Record<string, number> = { solid: 1, double: 7 };

function buildNumberFormat(settings: any): string | null {
  const decimals = Number.isInteger(settings?.decimalPlaces) ? settings.decimalPlaces : null;
  return buildNumberPattern(!!settings?.isPercentage, decimals);
}

function buildCellStyle(jsonCell: any): Record<string, any> | null {
  const style: Record<string, any> = {};

  if (jsonCell.component) {
    const theme = univerTheme[jsonCell.component as ComponentType];
    if (theme?.bgColor) style.bg = { rgb: theme.bgColor };
    if (theme?.numberFormat) style.n = { pattern: theme.numberFormat };
  }

  const fmtFromSettings = buildNumberFormat(jsonCell.settings);
  if (fmtFromSettings) style.n = { pattern: fmtFromSettings };

  const s = jsonCell.style;
  if (s) {
    if (s.color) {
      const rgb = FONT_COLOR_MAP[s.color] ?? s.color;
      style.cl = { rgb };
    }
    const align = HORIZONTAL_ALIGN_MAP[s['text-align']];
    if (align !== undefined) style.ht = align;
    if (s['font-weight'] === 'bold') style.bl = 1;
    if (s['font-style'] === 'italic') style.it = 1;
    // Univer models underline as ITextDecoration, so the flag lives on .s — not a bare 1.
    if (s['text-decoration'] === 'underline') style.ul = { s: 1 };

    const bd: Record<string, any> = {};
    (['top', 'bottom', 'left', 'right'] as const).forEach((side) => {
      const borderStyle = s[`border-${side}-style`];
      const key = BORDER_SIDE_KEY[side];
      if (borderStyle && BORDER_STYLE_MAP[borderStyle] && key) {
        bd[key] = { s: BORDER_STYLE_MAP[borderStyle], cl: { rgb: '#000000' } };
      }
    });
    if (Object.keys(bd).length) style.bd = bd;
  }

  return Object.keys(style).length ? style : null;
}

function resolveCellValue(jsonCell: any): string | number | null {
  if (jsonCell.component === 'NUMBER') {
    // Stored default value -> cell display (null = 'No Default' label, else the specific number).
    if (jsonCell.value == null || jsonCell.value === '') return 'No Default';
    const n = Number(jsonCell.value);
    return Number.isNaN(n) ? jsonCell.value : n;
  }
  if (jsonCell.component === 'DATETIME') {
    if (jsonCell.value == null) return 'Auto Update';
    if (jsonCell.value === 'CurrentDate') return 'Current Date';
    return jsonCell.value;
  }
  if (jsonCell.value != null && jsonCell.value !== '') return jsonCell.value;
  if (jsonCell.component) {
    const theme = univerTheme[jsonCell.component as ComponentType];
    if (theme?.defaultValue !== undefined) return theme.defaultValue;
  }
  return null;
}

class StyleRegistry {
  private byKey = new Map<string, string>();
  private records: Record<string, any> = {};
  private counter = 0;

  register(style: Record<string, any>): string {
    const key = JSON.stringify(style);
    const existing = this.byKey.get(key);
    if (existing) return existing;
    const id = `s${this.counter++}`;
    this.byKey.set(key, id);
    this.records[id] = style;
    return id;
  }

  toJSON(): Record<string, any> {
    return this.records;
  }
}

export interface CellRef {
  sheetId: string;
  row: number;
  col: number;
  component: string;
}

export interface LockedRange {
  sheetId: string;
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}


// Coalesce per-row runs of locked cells into ranges to keep rule count low.
export function computeLockedRangesBySheet(sheets: DbSheet[]): LockedRange[] {
  const out: LockedRange[] = [];

  sheets.forEach((sheet, sheetIndex) => {
    const sheetId = `sheet-${sheetIndex + 1}`;
    const rows = sheet.rows || [];

    let maxCol = 0;
    rows.forEach((row) => {
      row?.columns?.forEach((cell, c) => {
        if (cell && c > maxCol) maxCol = c;
      });
    });

    const columnCount = Math.max(maxCol + 1, MIN_COLUMNS);
    const rowCount = Math.max(rows.length, MIN_ROWS);

    const locked: boolean[][] = [];
    for (let r = 0; r < rows.length; r++) {
      locked[r] = new Array(columnCount).fill(true);
      const cols = rows[r]?.columns;
      if (cols) {
        cols.forEach((cell, c) => {
          if (!cell?.component) return;
          // Tokens stay editable; typed edits are blocked at runtime but they must stay replaceable.
          const editable = EDITABLE_COMPONENTS.has(cell.component)
            || LOCKED_COMPONENTS.has(cell.component);
          const isLong = cell.component === 'LONGLABEL' || cell.component === 'LONGTEXT';
          const rawSpan = cell.settings?.colspan ?? cell.settings?.colSpan;
          let span = rawSpan || 1;
          if (isLong && !rawSpan) span = Math.max(1, maxCol - c + 1);
          for (let i = 0; i < span && c + i < columnCount; i++) {
            locked[r][c + i] = !editable;
          }
        });
      }
    }

    for (let r = 0; r < rows.length; r++) {
      let spanStart = -1;
      for (let c = 0; c < columnCount; c++) {
        if (locked[r][c]) {
          if (spanStart < 0) spanStart = c;
        } else if (spanStart >= 0) {
          out.push({ sheetId, startRow: r, endRow: r, startColumn: spanStart, endColumn: c - 1 });
          spanStart = -1;
        }
      }
      if (spanStart >= 0) {
        out.push({ sheetId, startRow: r, endRow: r, startColumn: spanStart, endColumn: columnCount - 1 });
      }
    }

    if (rows.length < rowCount) {
      out.push({
        sheetId,
        startRow: rows.length,
        endRow: rowCount - 1,
        startColumn: 0,
        endColumn: columnCount - 1,
      });
    }
  });

  return out;
}

export function collectRuleCells(sheets: DbSheet[]): CellRef[] {
  const out: CellRef[] = [];
  sheets.forEach((sheet, sheetIndex) => {
    const sheetId = `sheet-${sheetIndex + 1}`;
    (sheet.rows || []).forEach((row, r) => {
      row?.columns?.forEach((cell, c) => {
        if (cell?.component) {
          const theme = univerTheme[cell.component as ComponentType];
          if (theme?.validation) {
            out.push({ sheetId, row: r, col: c, component: cell.component });
          }
        }
      });
    });
  });
  return out;
}

export function buildWorkbookSnapshot(unitId: string, sheets: DbSheet[]): any {
  const styles = new StyleRegistry();
  const workbookSheets: Record<string, any> = {};
  const sheetOrder: string[] = [];
  const commentsBySheet: Record<string, any[]> = {};
  let commentCounter = 0;
  const commentDT = formatCommentDate();

  sheets.forEach((sheet, sheetIndex) => {
    const sheetId = `sheet-${sheetIndex + 1}`;
    sheetOrder.push(sheetId);

    const cellData: Record<number, Record<number, any>> = {};
    const mergeData: Array<{ startRow: number; endRow: number; startColumn: number; endColumn: number }> = [];
    // LONG* without explicit colSpan resolve once columnCount is known.
    const pendingLongMerges: Array<{ row: number; col: number }> = [];
    // Univer renders border-right only on merge END cell.
    const pendingEndBorders: Array<{ row: number; col: number; border: any }> = [];
    let maxCol = 0;
    const rows = sheet.rows || [];

    rows.forEach((row, r) => {
      const columns = row?.columns;
      if (!columns) return;
      columns.forEach((jsonCell, c) => {
        if (!jsonCell) return;
        if (c > maxCol) maxCol = c;

        const cellObj: Record<string, any> = {};
        const value = resolveCellValue(jsonCell);

        if (value != null) {
          const strVal = String(value);
          if (strVal.startsWith('=') && !TEXT_COMPONENTS.has(jsonCell.component)) {
            cellObj.f = strVal;
          } else if (strVal.startsWith('=')) {
            // A text component keeps '=...' as content, so it belongs in v rather than f.
            // FORCE_STRING carries the seed's edit safety across a reload: without it, opening the
            // cell and pressing Enter commits '=...' back as a live formula.
            cellObj.v = strVal;
            cellObj.t = CellValueType.FORCE_STRING;
          } else {
            cellObj.v = value;
          }
        }

        const style = buildCellStyle(jsonCell);
        const rawColSpan = jsonCell.settings?.colspan ?? jsonCell.settings?.colSpan;
        if (style?.bd?.r && rawColSpan && rawColSpan > 1) {
          pendingEndBorders.push({ row: r, col: c + rawColSpan - 1, border: style.bd.r });
          delete style.bd.r;
          if (!Object.keys(style.bd).length) delete style.bd;
        }
        if (style && Object.keys(style).length) cellObj.s = styles.register(style);

        if (jsonCell.component) {
          cellObj.custom = { component: jsonCell.component };
        }

        const commentText = COMMENT_COMPONENTS[jsonCell.component as ComponentType];
        if (commentText) {
          const id = `tc-${sheetIndex}-${commentCounter++}`;
          const list = commentsBySheet[sheetId] || (commentsBySheet[sheetId] = []);
          list.push({
            id,
            threadId: id,
            ref: toA1(r, c),
            dT: commentDT,
            personId: 'system',
            text: {
              dataStream: `${commentText}\r\n`,
              paragraphs: [{ startIndex: commentText.length }],
              textRuns: [],
              customBlocks: [],
              customRanges: [],
              tables: [],
              sectionBreaks: [],
            },
            unitId,
            subUnitId: sheetId,
          });
        }

        if (Object.keys(cellObj).length) {
          if (!cellData[r]) cellData[r] = {};
          cellData[r][c] = cellObj;
        }

        const colSpan = jsonCell.settings?.colspan ?? jsonCell.settings?.colSpan;
        const isLong = jsonCell.component === 'LONGLABEL' || jsonCell.component === 'LONGTEXT';
        if (colSpan && colSpan > 1) {
          mergeData.push({
            startRow: r,
            endRow: r,
            startColumn: c,
            endColumn: c + colSpan - 1,
          });
        } else if (isLong) {
          pendingLongMerges.push({ row: r, col: c });
        }
      });
    });

    const columnCount = Math.max(maxCol + 1, MIN_COLUMNS);
    const rowCount = Math.max(rows.length, MIN_ROWS);

    pendingLongMerges.forEach(({ row, col }) => {
      if (col < maxCol) {
        mergeData.push({
          startRow: row,
          endRow: row,
          startColumn: col,
          endColumn: maxCol,
        });
      }
    });

    pendingEndBorders.forEach(({ row, col, border }) => {
      if (!cellData[row]) cellData[row] = {};
      const existing = cellData[row][col] || {};
      const existingStyle = existing.s ? { ...styles.toJSON()[existing.s] } : {};
      existingStyle.bd = { ...(existingStyle.bd || {}), r: border };
      existing.s = styles.register(existingStyle);
      cellData[row][col] = existing;
    });

    workbookSheets[sheetId] = {
      id: sheetId,
      name: sheet.name,
      cellData,
      mergeData,
      rowCount,
      columnCount,
      columnData: { 0: { w: FIRST_COLUMN_WIDTH } },
      defaultColumnWidth: DEFAULT_COLUMN_WIDTH,
      defaultRowHeight: 20,
    };
  });

  const resources: Array<{ name: string; data: string }> = [];
  if (Object.keys(commentsBySheet).length) {
    resources.push({
      name: THREAD_COMMENT_PLUGIN,
      data: JSON.stringify(commentsBySheet),
    });
  }

  return {
    id: unitId,
    sheets: workbookSheets,
    sheetOrder,
    styles: styles.toJSON(),
    name: unitId,
    appVersion: '3.0.0-alpha',
    locale: 'enUS',
    resources,
  };
}

const REVERSE_HALIGN: Record<number, string> = { 1: 'left', 2: 'center', 3: 'right' };
const REVERSE_BORDER_SIDE: Record<string, string> = { t: 'top', b: 'bottom', l: 'left', r: 'right' };
const REVERSE_BORDER_STYLE: Record<number, string> = { 1: 'solid', 7: 'double' };

const TOKEN_DEFAULTS: Record<string, string> = {
  COMPANY: '{COMPANY}', COUNTRY: '{COUNTRY}', CURRENCY: '{CURRENCY}', COMPANYDROPDOWN: '{COMPANYDROPDOWN}',
};

function cellToDbJson(fRange: any, mergeWidth: number): Record<string, any> {
  const meta = fRange.getCustomMetaData();
  const component: string | null = meta?.component ?? null;
  const styleData = fRange.getCellStyleData();
  const numPattern = (styleData?.n?.pattern as string) ?? '';

  let value: any = fRange.getValue();
  const formula = fRange.getFormula();
  if (formula) value = formula;

  if (component && TOKEN_DEFAULTS[component] && value === TOKEN_DEFAULTS[component]) {
    value = null;
  }

  if (component === 'DATETIME') {
    if (value === 'Auto Update' || value === DATETIME_SPECIFIC_SENTINEL) value = null;
    else if (value === 'Current Date') value = 'CurrentDate';
  }

  if (component === 'NUMBER') {
    // Cell display -> stored default value ('No Default'/sentinel = null, else the specific
    // number as a string). Percentage / decimal formatting persists separately via numPattern.
    if (value === 'No Default' || value === NUMBER_SPECIFIC_SENTINEL || value == null || value === '') value = null;
    else value = String(value);
  }

  const cell: Record<string, any> = { component, value };

  if (styleData) {
    const style: Record<string, any> = {};

    if (styleData.cl?.rgb) {
      style.color = mapHexToPrimeVueColor(styleData.cl.rgb);
    }
    if (styleData.ht !== undefined && REVERSE_HALIGN[styleData.ht]) {
      style['text-align'] = REVERSE_HALIGN[styleData.ht];
    }
    if (styleData.bl === 1) {
      style['font-weight'] = 'bold';
    }
    if (styleData.it === 1) {
      style['font-style'] = 'italic';
    }
    if (styleData.ul?.s === 1) {
      style['text-decoration'] = 'underline';
    }
    if (styleData.bd) {
      for (const [key, side] of Object.entries(REVERSE_BORDER_SIDE)) {
        const border = (styleData.bd as any)[key];
        if (border?.s && REVERSE_BORDER_STYLE[border.s]) {
          const isThick = border.s === 7;
          style[`border-${side}`] = 'black';
          style[`border-${side}-style`] = REVERSE_BORDER_STYLE[border.s];
          style[`border-${side}-width`] = isThick ? '4px' : '1.5px';
        }
      }
    }

    if (Object.keys(style).length) cell.style = style;
  }

  const settings: Record<string, any> = {};

  if ((component === 'LONGLABEL' || component === 'LONGTEXT') && mergeWidth > 1) {
    settings.colspan = mergeWidth;
  }

  if (component === 'NUMBER' || component === 'FORMULA' || component === 'VALUE') {
    if (numPattern) {
      const decMatch = numPattern.match(/\.([0]+)/);
      settings.decimalPlaces = decMatch ? decMatch[1].length : 0;
      if (numPattern.includes('%')) settings.isPercentage = true;
    } else if (component === 'NUMBER') {
      settings.decimalPlaces = 'auto';
    }
  }

  if (Object.keys(settings).length) cell.settings = settings;

  return cell;
}

export function extractWorkbookToDbJson(univerAPI: any): Array<{ univerSheetId: string; sheetName: string; content: any }> {
  const fWorkbook = univerAPI.getActiveWorkbook();
  if (!fWorkbook) return [];

  const result: Array<{ univerSheetId: string; sheetName: string; content: any }> = [];
  const sheets = fWorkbook.getSheets();

  for (const fWorksheet of sheets) {
    const sheetName = fWorksheet.getSheetName();
    const rowCount = fWorksheet.getMaxRows();
    const colCount = fWorksheet.getMaxColumns();

    let maxRow = -1;
    let maxCol = -1;
    for (let r = 0; r < Math.min(rowCount, 500); r++) {
      for (let c = 0; c < Math.min(colCount, 50); c++) {
        const range = fWorksheet.getRange(r, c);
        const meta = range.getCustomMetaData();
        const val = range.getValue();
        if (meta?.component || (val != null && val !== '')) {
          if (r > maxRow) maxRow = r;
          if (c > maxCol) maxCol = c;
        }
      }
    }

    const template: Array<{ columns: any[] }> = [];

    for (let r = 0; r <= maxRow; r++) {
      const columns: any[] = [];
      for (let c = 0; c <= maxCol; c++) {
        const mergeRange = fWorksheet.getCellMergeData(r, c);
        if (mergeRange) {
          const mRange = mergeRange.getRange();
          if (r !== mRange.startRow || c !== mRange.startColumn) {
            columns.push({ component: null, value: null });
            continue;
          }
          const mergeWidth = mRange.endColumn - mRange.startColumn + 1;
          const cell = cellToDbJson(fWorksheet.getRange(r, c), mergeWidth);
          // DB stores border-right on merge start; editor has it on merge end.
          if (mergeWidth > 1) {
            const endStyle = fWorksheet.getRange(r, mRange.endColumn).getCellStyleData();
            const endBorderR = endStyle?.bd?.r;
            if (endBorderR?.s && REVERSE_BORDER_STYLE[endBorderR.s]) {
              const isThick = endBorderR.s === 7;
              cell.style = cell.style || {};
              cell.style['border-right'] = 'black';
              cell.style['border-right-style'] = REVERSE_BORDER_STYLE[endBorderR.s];
              cell.style['border-right-width'] = isThick ? '4px' : '1.5px';
            }
          }
          columns.push(cell);
        } else {
          columns.push(cellToDbJson(fWorksheet.getRange(r, c), 1));
        }
      }
      template.push({ columns });
    }

    result.push({
      univerSheetId: fWorksheet.getSheetId(),
      sheetName,
      content: { actions: [], template },
    });
  }

  return result;
}
